


  /**
 * transformFromStaticToNodesAndEdges
 * ----------------------------------
 * Diese Funktion nimmt ein JSON-Objekt entgegen, das für die Visualisierung
 * in einem Flowchart-Tool wie React Flow vorbereitet wurde ("staticJSON").
 * Sie erzeugt darauf basierend zwei Arrays: "nodes" und "edges".
 *
 * @param {Object} staticJSON - das "zusammengepuzzelte" JSON, 
 *                              typischerweise aus translateFromRawTamplateToStaticJson().
 *
 * Erwarter Aufbau von staticJSON:
 *  {
 *    id: "rec_12345",        // (z.B. DB-Record-ID)
 *    name: "Mein Flow oder Gruppe",
 *    lvl1_connections: [     // Verbindungen zwischen top-level Blocks (Flow-Ebene)
 *      { from: "grp1", to: "taskXYZ" }
 *      ... 
 *    ],
 *    blocks: [               // Eine Liste von Objekten, die entweder
 *      {                     // 1) "group" oder 2) "task" sind
 *        type: "group",
 *        instance_id: "grp1",
 *        instance_pos: { x: 100, y: 150 },
 *        template_id: "rec_33333",
 *        name: "Beispiel-Gruppe",
 *        children_connections: [ { from: "tA", to: "tB" } ],
 *        children_blocks: [
 *          {
 *            type: "task",
 *            instance_id: "tA",
 *            instance_pos: { x: 100, y: 150 },
 *            template_id: "rec_1111",
 *            name: "Task 1",
 *            clickup_vorlage_id: "tmp123",
 *            rolle: "Designer"
 *          },
 *          ...
 *        ]
 *      },
 *      {
 *        type: "task",
 *        instance_id: "taskXYZ",
 *        instance_pos: { x: 500, y: 150 },
 *        template_id: "rec_5555555",
 *        name: "Task 3",
 *        clickup_vorlage_id: "tmp999",
 *        rolle: "Tester"
 *      },
 *      ...
 *    ]
 *  }
 *
 * Der Rückgabewert ist ein Objekt mit:
 *  {
 *    nodes: [...],
 *    edges: [...]
 *  }
 *
 * - nodes[] enthält alle zu rendernden Knoten (Tasks auf oberster Ebene, Gruppen,
 *   sowie Tasks innerhalb von Gruppen).
 * - edges[] enthält alle Kanten, sowohl "lvl1_connections" auf Flow-Ebene
 *   als auch "children_connections" innerhalb einer Gruppe.
 *
 * Der Code geht dazu folgendermaßen vor:
 *  1) Prüfen, ob staticJSON existiert. Falls nicht, leeres Ergebnis zurückgeben.
 *  2) Aus staticJSON die Felder "lvl1_connections" und "blocks" extrahieren.
 *  3) In einer Schleife die "blocks" durchlaufen:
 *     - Wenn ein Block "type=group" ist -> erstelle einen "groupNode" in "nodes".
 *       * Anschließend alle "children_blocks" durchgehen und daraus Task-Nodes
 *         erzeugen. Dabei wird die Position jedes Child-Tasks relativ
 *         zur Gruppen-Position berechnet.
 *       * Außerdem "children_connections" in edges überführen (z.B. tA->tB).
 *     - Wenn ein Block "type=task" ist -> Top-Level Task-Node in "nodes".
 *  4) Abschließend "lvl1_connections" (auf Flow-Ebene) in edges übertragen.
 *  5) nodes und edges zurückgeben.
 */
  export const transformFromStaticToNodesAndEdges = (staticJSON, modus, isTemplateEditing, handleEditGroup, handleEditNode, handleNodeDelete, handleChildrenEdgeCut,  currentEditingGroupId, workspaceRoles, isDragging) => {
    // Wenn kein passendes Objekt, dann leere Ausgabe
    if (!staticJSON) {
      return { nodes: [], edges: [] };
    }
  
    console.log("start transformation: ", staticJSON)

    // Extract: Verbindungen auf oberster Ebene + Liste aller Blöcke
    const { lvl1_connections, blocks } = staticJSON;
  
    let nodes = [];
    let edges = [];
  
    // Hilfsfunktion, um eine eindeutige ID für Kind-Knoten zu generieren
    // z.B. "grp1::tA"
    const makeChildId = (parentId, childId) => `${parentId}::${childId}`;
  
    // Durch alle Blocks auf oberster Ebene iterieren
    blocks.forEach((block) => {
      // === Fall A: Gruppe ===
      
      if (block.type === 'group') {

        // 1) Finde das Bounding-Box-Rechteck aller Kinder
        const margin = 50; // z.B. 30px Puffer
        let minX = Infinity, maxX = -Infinity;
        let minY = Infinity, maxY = -Infinity;
        let groupPosition = {
          x: block.instance_pos?.x || 0,
          y: block.instance_pos?.y || 0
        }
        // Prüfen, ob es Child-Tasks gibt
        if (Array.isArray(block.children_blocks) && block.children_blocks.length > 0) {
          block.children_blocks.forEach((childTask) => {
            // Absolute Koordinaten = Gruppen-Pos + Child-Pos
            const childAbsX = (block.instance_pos?.x || 0) + (childTask.instance_pos?.x || 0);
            const childAbsY = (block.instance_pos?.y || 0) + (childTask.instance_pos?.y || 0);

            if (childAbsX < minX) minX = childAbsX;
            if (childAbsX > maxX) maxX = childAbsX;
            if (childAbsY < minY) minY = childAbsY;
            if (childAbsY > maxY) maxY = childAbsY;
          });
        }

        // 2) Wenn keine Kinder, definieren wir einfach default-Größe
        let boundingWidth = 200;
        let boundingHeight = 150;



        // Wenn minX noch Infinity ist, heißt das: keine Kinder -> bleibe bei Default
        if (minX !== Infinity && minY !== Infinity) {
          boundingWidth = (maxX - minX) + (margin * 2) + window.WORKFLOWBUILDER_CHILD_WIDTH;
          boundingHeight = (maxY - minY) + (margin * 2) + window.WORKFLOWBUILDER_CHILD_HEIGHT - (margin / 4);

          const freezePosition = isDragging && currentEditingGroupId === block.instance_id;
          groupPosition = freezePosition
            ? {
                x: block.instance_pos?.x || 0,
                y: block.instance_pos?.y || 0
              }
            : {
                x: minX - margin,
                y: minY - margin
              };
        }

        console.log("gen group: ", currentEditingGroupId, " - ", block.instance_id)
        console.log("isTemplateEditing: ", isTemplateEditing)
        // 1) Erzeuge einen groupNode (repräsentiert die Gruppe als "Container")

        let onEditNode = (modus=="editFlow" && isTemplateEditing==false && currentEditingGroupId !== block.instance_id)? handleEditNode : null;
            
        console.log("onEditNode: ", onEditNode)

        nodes.push({
          id: block.instance_id,     // z.B. "grp1"
          type: 'groupNode',         // React Flow Node-Typ
          label: block.name || 'Group',
          position: groupPosition,
          data: {
            instance_id: block.instance_id,  
            template_id: block.template_id,
            isTemplateEditing: isTemplateEditing,
            modus: modus,
            label: block.name || 'unknown name',

            onEditGroup: (modus=="editGroup" || modus=="editFlow" && isTemplateEditing==false) ? handleEditGroup : null,
            onEditNode: onEditNode,

            onDeleteNode: currentEditingGroupId === block.instance_id ? null : handleNodeDelete,
            isDraggable: currentEditingGroupId !== block.instance_id ? true : false,
            isCurrentInEditMode: currentEditingGroupId === block.instance_id ? true : false,


            // NEU: Bounding-Box-Infos
            boundingWidth: boundingWidth,
            boundingHeight: boundingHeight
          },
          dragHandle: '.drag-handle__custom'
        });
  
        // 2) Falls die Gruppe child-Tasks hat (children_blocks), erzeuge für
        //    jeden Task einen separaten Node
        if (Array.isArray(block.children_blocks)) {



          block.children_blocks.forEach((childTask) => {

            // Eindeutige ID = parentID::childID, z.B. "grp1::tA"
            const childId = makeChildId(block.instance_id, childTask.instance_id);
  
            nodes.push({
              id: childId,
              type: 'taskNode',
              label: childTask.name || 'Task',
              // Position relativ zum Gruppen-Node
              position: {
                x: (block.instance_pos?.x || 0) + (childTask.instance_pos?.x || 0),
                y: (block.instance_pos?.y || 0) + (childTask.instance_pos?.y || 0)
              },
              data: {
                duration_days: childTask.duration_days || 0,
                template_id: childTask.template_id,
                label: childTask.name || 'Task',
                clickup_vorlage_id: childTask.clickup_vorlage_id,
                rolle: childTask.rolle || 'unbekannte Rolle',
                instance_id: childTask.instance_id,
                parentId: block.instance_id, 
                onCut: currentEditingGroupId === block.instance_id ? handleChildrenEdgeCut : null,
                onDeleteNode: currentEditingGroupId === block.instance_id ? handleNodeDelete : null,
                onEditNode: currentEditingGroupId === block.instance_id && isTemplateEditing==false ? handleEditNode : null,
                isDraggable: currentEditingGroupId === block.instance_id ? true : false,
              },
              dragHandle: '.drag-handle__custom'
            });
          });
        }
  


        // 3) "children_connections" innerhalb der Gruppe => edges
        if (Array.isArray(block.children_connections)) {
          block.children_connections.forEach((conn, idx) => {
            // z.B. conn.from = "tA", conn.to = "tB"
            // => "grp1::tA" -> "grp1::tB"
            const sourceId = makeChildId(block.instance_id, conn.from);
            const targetId = makeChildId(block.instance_id, conn.to);
  
            edges.push({
              id: `${block.instance_id}::edge${idx}`,
              source: sourceId,
              target: targetId
            });
          });
        }
  
      // === Fall B: Task auf oberster Ebene ===
      } else if (block.type === 'task') {
        // Dieser Task wird direkt als Node ohne Gruppe eingetragen
        nodes.push({
          id: block.instance_id,
          type: 'taskNode',
          label: block.name || 'Task',
          position: {
            x: block.instance_pos?.x || 0,
            y: block.instance_pos?.y || 0
          },
          data: {
            duration_days: block.duration_days || 0,
            template_id: block.template_id,
            clickup_vorlage_id: block.clickup_vorlage_id,
            rolle: block.rolle,
            label: block.name || 'Task',
            instance_id: block.instance_id,
            onEditNode: isTemplateEditing==false ? handleEditNode : null,
            onDeleteNode: handleNodeDelete,
            isDraggable: true,
          },
          dragHandle: '.drag-handle__custom'
        });
      }
    });
  
    // === Fall C: Flow-Ebene connections ===
    //    z.B. {from: "grp1", to: "taskXYZ"}
    if (Array.isArray(lvl1_connections)) {
      lvl1_connections.forEach((conn, idx) => {
        edges.push({
          id: `flowEdge${idx}`,
          source: conn.from,
          target: conn.to
        });
      });
    }
  
    console.log("Nodes:", nodes);
    console.log("Edges:", edges);
  
    // Das fertige Objekt {nodes, edges} wird von React Flow oder ähnlichen Libs erwartet
    return { nodes, edges };
  };
  


